import {
  getSystemDoc,
  getSystemDocIndex,
  SystemDocGroup,
  SystemDocItem,
} from '../../api/systemDocsApi'
import NavBar from '../../components/nav-bar/nav-bar'
import styles from './index.module.less'
import { Card, Empty, Input, Spin } from 'antd'
import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

type MarkdownNode = {
  key: string
  type: 'heading' | 'paragraph' | 'list' | 'table' | 'code'
  level?: number
  text?: string
  language?: string
  items?: string[]
  header?: string[]
  rows?: string[][]
}

type MermaidApi = typeof import('mermaid').default

let isMermaidInitialized = false
let mermaidApi: MermaidApi | null = null

const loadMermaid = async () => {
  if (!mermaidApi) {
    const mermaidModule = await import('mermaid')
    mermaidApi = mermaidModule.default
  }

  if (isMermaidInitialized) {
    return mermaidApi
  }

  mermaidApi.initialize({
    startOnLoad: false,
    securityLevel: 'strict',
    theme: 'default',
    flowchart: {
      htmlLabels: false,
    },
  })
  isMermaidInitialized = true

  return mermaidApi
}

const createDiagramId = (key: string) => {
  const hash = key.split('').reduce((value, character) => {
    return (value * 31 + character.charCodeAt(0)) >>> 0
  }, 0)

  return `system-doc-diagram-${hash}`
}

const removeMermaidArtifact = (diagramId: string) => {
  document.getElementById(diagramId)?.remove()
}

const splitInlineCode = (value: string) =>
  value.split(/(`[^`]+`)/g).map((part, index) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code className={styles['inline-code']} key={`${part}-${index}`}>
          {part.slice(1, -1)}
        </code>
      )
    }
    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
  })

const MermaidDiagram = ({ code, idSeed }: { code: string; idSeed: string }) => {
  const [svgUrl, setSvgUrl] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    let objectUrl = ''

    setSvgUrl('')
    setError('')

    const renderDiagram = async () => {
      const diagramId = createDiagramId(idSeed)

      try {
        const mermaid = await loadMermaid()
        await mermaid.parse(code, { suppressErrors: false })
        const { svg } = await mermaid.render(diagramId, code)
        removeMermaidArtifact(diagramId)
        objectUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }))

        if (active) {
          setSvgUrl(objectUrl)
        } else {
          URL.revokeObjectURL(objectUrl)
        }
      } catch (exception) {
        removeMermaidArtifact(diagramId)

        if (active) {
          setError(exception instanceof Error ? exception.message : 'Mermaid rendering failed')
        }
      }
    }

    renderDiagram()

    return () => {
      active = false
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [code, idSeed])

  if (error) {
    return (
      <div className={styles['mermaid-block']}>
        <div className={styles['mermaid-error-title']}>Mermaid 渲染失败，已显示源码</div>
        <div className={styles['mermaid-error-message']}>{error}</div>
        <pre>
          <code>{code}</code>
        </pre>
      </div>
    )
  }

  if (!svgUrl) {
    return <div className={styles['mermaid-loading']}>正在渲染图表...</div>
  }

  return (
    <div className={styles['mermaid-diagram']}>
      <img alt="Mermaid diagram" src={svgUrl} />
    </div>
  )
}

const isTableSeparator = (line: string) =>
  /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line)

const parseTableRow = (line: string) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(cell => cell.trim())

const parseMarkdown = (markdown: string): MarkdownNode[] => {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n')
  const nodes: MarkdownNode[] = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index]
    const key = `${index}-${line}`

    if (line.startsWith('```')) {
      const language = line.slice(3).trim().toLowerCase()
      const code: string[] = []
      index += 1
      while (index < lines.length && !lines[index].startsWith('```')) {
        code.push(lines[index])
        index += 1
      }
      index += 1
      nodes.push({ key, type: 'code', language, text: code.join('\n') })
      continue
    }

    if (!line.trim()) {
      index += 1
      continue
    }

    if (line.startsWith('# ')) {
      nodes.push({ key, type: 'heading', level: 1, text: line.slice(2) })
      index += 1
      continue
    }

    if (line.startsWith('## ')) {
      nodes.push({ key, type: 'heading', level: 2, text: line.slice(3) })
      index += 1
      continue
    }

    if (line.startsWith('### ')) {
      nodes.push({ key, type: 'heading', level: 3, text: line.slice(4) })
      index += 1
      continue
    }

    if (line.includes('|') && index + 1 < lines.length && isTableSeparator(lines[index + 1])) {
      const header = parseTableRow(line)
      const rows: string[][] = []
      index += 2
      while (index < lines.length && lines[index].includes('|') && lines[index].trim()) {
        rows.push(parseTableRow(lines[index]))
        index += 1
      }
      nodes.push({ key, type: 'table', header, rows })
      continue
    }

    if (line.startsWith('- ')) {
      const items: string[] = []
      while (index < lines.length && lines[index].startsWith('- ')) {
        items.push(lines[index].slice(2))
        index += 1
      }
      nodes.push({ key, type: 'list', items })
      continue
    }

    nodes.push({ key, type: 'paragraph', text: line })
    index += 1
  }

  return nodes
}

const renderMarkdownNode = (node: MarkdownNode): ReactNode => {
  if (node.type === 'heading' && node.level === 1) {
    return <h1 key={node.key}>{node.text}</h1>
  }
  if (node.type === 'heading' && node.level === 2) {
    return <h2 key={node.key}>{node.text}</h2>
  }
  if (node.type === 'heading' && node.level === 3) {
    return <h3 key={node.key}>{node.text}</h3>
  }
  if (node.type === 'paragraph') {
    return <p key={node.key}>{splitInlineCode(node.text || '')}</p>
  }

  if (node.type === 'list') {
    return (
      <ul key={node.key}>
        {node.items?.map(item => (
          <li key={item}>{splitInlineCode(item)}</li>
        ))}
      </ul>
    )
  }

  if (node.type === 'table') {
    return (
      <table key={node.key}>
        <thead>
          <tr>
            {node.header?.map(cell => (
              <th key={cell}>{splitInlineCode(cell)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {node.rows?.map((row, rowIndex) => (
            <tr key={`${node.key}-${rowIndex}`}>
              {row.map((cell, cellIndex) => (
                <td key={`${cell}-${cellIndex}`}>{splitInlineCode(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  if (node.type === 'code') {
    if (node.language === 'mermaid') {
      return <MermaidDiagram code={node.text || ''} idSeed={node.key} key={node.key} />
    }

    return (
      <pre className={styles['code-block']} key={node.key}>
        <code>{node.text}</code>
      </pre>
    )
  }

  return null
}

const filterGroups = (groups: SystemDocGroup[], keyword: string) => {
  const normalizedKeyword = keyword.trim().toLowerCase()
  if (!normalizedKeyword) {
    return groups
  }

  return groups
    .map(group => ({
      ...group,
      docs: group.docs.filter(doc =>
        [doc.title, doc.path, doc.description].some(value =>
          value?.toLowerCase().includes(normalizedKeyword),
        ),
      ),
    }))
    .filter(group => group.docs.length)
}

const findDoc = (groups: SystemDocGroup[], path: string) =>
  groups.flatMap(group => group.docs).find(doc => doc.path === path)

const SystemDocs = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [groups, setGroups] = useState<SystemDocGroup[]>([])
  const [defaultDoc, setDefaultDoc] = useState('')
  const [selectedPath, setSelectedPath] = useState('')
  const [markdown, setMarkdown] = useState('')
  const [keyword, setKeyword] = useState('')
  const [loadingIndex, setLoadingIndex] = useState(true)
  const [loadingDoc, setLoadingDoc] = useState(false)

  useEffect(() => {
    getSystemDocIndex()
      .then(index => {
        const initialPath = searchParams.get('doc') || index.defaultDoc
        setGroups(index.groups)
        setDefaultDoc(index.defaultDoc)
        setSelectedPath(initialPath)
      })
      .finally(() => setLoadingIndex(false))
  }, [])

  useEffect(() => {
    if (!selectedPath) {
      return
    }

    setLoadingDoc(true)
    getSystemDoc(selectedPath)
      .then(content => setMarkdown(content))
      .finally(() => setLoadingDoc(false))
  }, [selectedPath])

  const visibleGroups = useMemo(() => filterGroups(groups, keyword), [groups, keyword])
  const selectedDoc = findDoc(groups, selectedPath) || findDoc(groups, defaultDoc)
  const markdownNodes = useMemo(() => parseMarkdown(markdown), [markdown])

  const selectDoc = (doc: SystemDocItem) => {
    setSelectedPath(doc.path)
    setSearchParams({ doc: doc.path })
  }

  return (
    <div className={`dashboard-wrapper ${styles.page}`}>
      <NavBar title="系统生态文档" />
      <Card className={styles.card}>
        <Spin spinning={loadingIndex}>
          <div className={styles.layout}>
            <aside className={styles.sidebar}>
              <Input
                className={styles.search}
                allowClear
                placeholder="搜索文档"
                value={keyword}
                onChange={event => setKeyword(event.target.value)}
              />
              {visibleGroups.map(group => (
                <div key={group.title}>
                  <div className={styles['group-title']}>{group.title}</div>
                  {group.docs.map(doc => (
                    <button
                      className={`${styles['doc-button']} ${
                        doc.path === selectedPath ? styles['doc-button-active'] : ''
                      }`}
                      key={doc.path}
                      type="button"
                      onClick={() => selectDoc(doc)}
                    >
                      <div className={styles['doc-title']}>{doc.title}</div>
                      {doc.description && (
                        <div className={styles['doc-description']}>{doc.description}</div>
                      )}
                    </button>
                  ))}
                </div>
              ))}
              {!visibleGroups.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            </aside>
            <main className={styles.content}>
              <Spin spinning={loadingDoc}>
                {selectedDoc && (
                  <div className={styles['content-header']}>
                    <div className={styles['content-title']}>{selectedDoc.title}</div>
                    {selectedDoc.description && (
                      <div className={styles['doc-description']}>{selectedDoc.description}</div>
                    )}
                  </div>
                )}
                <div className={styles.markdown}>
                  {markdownNodes.map(node => renderMarkdownNode(node))}
                </div>
              </Spin>
            </main>
          </div>
        </Spin>
      </Card>
    </div>
  )
}

export default SystemDocs
